import axios from 'axios';
import cfac22 from '../../cfac22';
import { clearStoredAuthData, clearAuthFlowLocalContext } from 'js/library/services/authSession';
import { refreshAccessToken } from './authSessionManager';

export const VALIDATE_SESSIONS_THROTTLE_MS = 30 * 1000;

let lastValidateSessionsAt = 0;
let lastValidateSessionsResult = null;
let lastValidateSessionsError = null;
let validateSessionsInFlightPromise = null;

const clearInvalidSession = () => {
  clearStoredAuthData();
  clearAuthFlowLocalContext();

  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

const isExplicitlyInvalid = (data, validationData) =>
  data?.success === false ||
  data?.status === false ||
  data?.status === 'error' ||
  data?.status === 'failed' ||
  data?.valid === false ||
  validationData?.success === false ||
  validationData?.status === false ||
  validationData?.status === 'error' ||
  validationData?.status === 'failed' ||
  validationData?.valid === false;

const isValidSessionResponse = (data, validationData) => {
  if (isExplicitlyInvalid(data, validationData)) return false;

  if (
    data?.success === true ||
    data?.status === true ||
    data?.status === 'success' ||
    validationData?.success === true ||
    validationData?.status === true ||
    validationData?.status === 'success' ||
    data?.valid === true ||
    validationData?.valid === true
  ) {
    return true;
  }

  const message = String(data?.message || validationData?.message || '').toLowerCase();
  const sessionId = data?.sessionId || validationData?.sessionId;

  return Boolean(sessionId && /sess[aã]o\s+v[aá]lida/.test(message));
};

export const validateSessions = async ({ force = false, refreshOnRequired = true } = {}) => {
  const now = Date.now();

  if (validateSessionsInFlightPromise) {
    return validateSessionsInFlightPromise;
  }

  if (!force && now - lastValidateSessionsAt < VALIDATE_SESSIONS_THROTTLE_MS) {
    if (lastValidateSessionsError) {
      throw lastValidateSessionsError;
    }

    if (lastValidateSessionsResult) {
      return lastValidateSessionsResult;
    }
  }

  lastValidateSessionsAt = now;

  const runValidation = async () => {
    try {
      const endpoint = `${cfac22('MICROSERVICES')}/auth/sessions/validate`;

      const { data } = await axios.get(endpoint, {
        withCredentials: true,
        headers: {
          'Full-Url': window.location.href,
        },
      });

      const error = data?.error || data?.message;
      const validationData = data?.result || data?.data || data || {};
      const requiresRefresh =
        data?.requiresRefresh === true || validationData?.requiresRefresh === true;

      if (requiresRefresh && refreshOnRequired) {
        const accessToken = await refreshAccessToken();

        return {
          success: true,
          requiresRefresh: true,
          data: {
            ...validationData,
            accessToken,
          },
        };
      }

      // requiresRefresh=true is the explicit signal that an invalid/expired
      // token belongs to a session that can still be renewed.
      if (requiresRefresh) {
        return {
          success: true,
          requiresRefresh: true,
          data: validationData,
        };
      }

      const normalizedSessionStatus = String(validationData?.status || '').toLowerCase();
      const hasInvalidSessionStatus =
        normalizedSessionStatus && !['active', 'success'].includes(normalizedSessionStatus);

      if (!isValidSessionResponse(data, validationData) || hasInvalidSessionStatus) {
        clearInvalidSession();
        return {
          success: false,
          error: error || 'Erro ao validar sessão',
        };
      }

      return {
        success: true,
        data: validationData,
      };
    } catch (error) {
      clearInvalidSession();
      throw error?.response?.data || error;
    }
  };

  validateSessionsInFlightPromise = runValidation();

  try {
    const result = await validateSessionsInFlightPromise;
    // Uma resposta que exige refresh é transitória e não deve ser reutilizada
    // em novas chamadas durante a janela do throttle.
    lastValidateSessionsResult = result?.requiresRefresh === true ? null : result;
    lastValidateSessionsError = null;
    return result;
  } catch (error) {
    lastValidateSessionsError = error;
    lastValidateSessionsResult = null;
    throw error;
  } finally {
    validateSessionsInFlightPromise = null;
  }
};
